/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { DndContext, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import SettingsIcon from "@druva-apps/orion-icons/lib/SortingcolumnGreyOutline";

import Popover from "../../popover";
import { useTable } from "../context";
import { reorderColumns } from "../actions";
import { extractColumns, rebuildColumnsDef, shuffleDndItems } from "../utils";
import ColumnGroup from "./SortableGroup";
import Column from "./SortableColumn";

const PopoverBody = ({ height }) => {
  const { columns, fireAction } = useTable();

  const items = rebuildColumnsDef(columns);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10
      }
    })
  );

  /* istanbul ignore next */
  // We cannot test this function in an environment like jsdom where layouts are not rendered
  // Will test in other e2e testing framework in future like cypress for Orion.
  const onDragEnd = useCallback(
    parent => event => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        let shuffled = [];

        if (parent) {
          shuffled = items.map(item => {
            if (item.key === parent) {
              item.children = shuffleDndItems(item.children, active, over);
            }
            return item;
          });
        } else {
          shuffled = shuffleDndItems(items, active, over);
        }
        const flattened = extractColumns(shuffled);
        fireAction(reorderColumns(flattened));
      }
    },
    [fireAction, items]
  );

  return (
    <div
      className="oui-data-table-settings-panel"
      style={{ height: `${height}px` }}
    >
      <p>Add and remove the columns to customize.</p>
      <DndContext
        sensors={sensors}
        onDragEnd={onDragEnd()}
        modifiers={[restrictToFirstScrollableAncestor]}
      >
        <SortableContext items={items.map(i => i.key)}>
          {items.map(item => {
            if (item.children && item.key !== "__action__") {
              return (
                <ColumnGroup id={item.key} data={item} key={item.key}>
                  <DndContext
                    sensors={sensors}
                    onDragEnd={onDragEnd(item.key)}
                    modifiers={[restrictToFirstScrollableAncestor]}
                  >
                    <SortableContext items={item.children.map(c => c.key)}>
                      {item.children.map(child => (
                        <Column id={child.key} key={child.key} data={child} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </ColumnGroup>
              );
            }

            return (
              item.key !== "__action__" && (
                <Column id={item.key} key={item.key} data={item} />
              )
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
};

PopoverBody.propTypes = {
  height: PropTypes.number
};

const Settings = ({ height, className }) => (
  <Popover
    size="sm"
    placement="bottom"
    type="uncontrolled"
    title="Edit Columns"
    body={() => <PopoverBody height={height} />}
    isOpen
  >
    <button
      type="button"
      aria-label="Settings Panel Icon"
      className={cx("orion-data-grid__navigation--empty-button", className)}
    >
      <SettingsIcon size="sm" clickable />
    </button>
  </Popover>
);

Settings.propTypes = {
  height: PropTypes.number,
  className: PropTypes.string
};

Settings.defaultProps = {
  height: 400
};

export default Settings;
